import { axiosWithRetryInstance } from "@/api/base";

export interface CellPosition {
  rowIndex: number;
  columnIndex: number;
}

interface WindowBoundsInput {
  rowCount: number;
  columnCount: number;
  maxRows: number;
  maxColumns: number;
  target?: CellPosition | null;
}

// Must use nginx's `/files/` route: the backend endpoint replies with an empty
// body plus X-Accel-Redirect headers that only nginx resolves into the GCS
// bytes; the direct /pf-mainweb route bypasses nginx and yields a 0-byte blob.
export const getExcelContent = async (fseId: string) =>
  axiosWithRetryInstance.get<Blob>("/files/document/original", {
    responseType: "blob",
    params: { fse_id: fseId },
  });

export const getColumnLabel = (columnIndex: number): string => {
  let label = "";
  let value = columnIndex + 1;

  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }

  return label;
};

export const parseCellRef = (cellRef: string): CellPosition | null => {
  const match = /^([A-Z]+)(\d+)$/i.exec(cellRef.trim());
  if (!match) return null;

  const columnLetters = match[1].toUpperCase();
  const rowNumber = Number(match[2]);
  if (!rowNumber || rowNumber < 1) return null;

  let columnIndex = 0;
  for (const letter of columnLetters) {
    columnIndex = columnIndex * 26 + letter.charCodeAt(0) - 64;
  }

  return {
    rowIndex: rowNumber - 1,
    columnIndex: columnIndex - 1,
  };
};

export const getWindowBounds = ({ rowCount, columnCount, maxRows, maxColumns, target }: WindowBoundsInput) => {
  const rowWindowSize = Math.min(rowCount, maxRows);
  const columnWindowSize = Math.min(columnCount, maxColumns);
  const rowStart = getWindowStart(rowCount, rowWindowSize, target?.rowIndex);
  const columnStart = getWindowStart(columnCount, columnWindowSize, target?.columnIndex);

  return {
    rowStart,
    rowEnd: rowStart + rowWindowSize,
    columnStart,
    columnEnd: columnStart + columnWindowSize,
  };
};

const getWindowStart = (total: number, windowSize: number, targetIndex?: number): number => {
  if (targetIndex === undefined || targetIndex < 0 || targetIndex >= total || total <= windowSize) {
    return 0;
  }

  const centeredStart = targetIndex - Math.floor(windowSize / 2);
  return Math.min(Math.max(centeredStart, 0), total - windowSize);
};
